/* GrowthOS — Account record page.
   Re-skinned to mirror the Lead/Contact detail (`.gos-leadrec` shell): a back row,
   header card (avatar, name, Call button, dots menu, chips), a horizontal tab bar
   and a read-only Overview + sticky Details panel. Tabs reuse the SHARED child
   components (Timeline, Notes, Attachments, Email, the themed Opportunities — same
   component now used by Contact detail). Tags use the real Account tag APIs.
   Contacts / Member Accounts tabs are ported from the latest claude.ai/design
   `growthos/AccountRecord.jsx` (Contacts + MemberAccounts) into
   `AccountContactsTab.jsx` / `AccountMemberAccountsTab.jsx` — composed entirely of
   existing `gos-oppr-*`/`gos-set-table*`/`gos-bulk-ava` classes (no new CSS), wired
   to the real `getContactByModule`/`getChildAccountByParentAccountId` APIs, replacing
   the legacy unstyled `ChildContact`/`memberAccount.jsx` AG-grids on this page (those
   files are untouched — still used elsewhere). Enrichment + the account-only
   "Generate Report" feature are preserved exactly (same APIs), moved into the header
   dots menu. */
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
  fetchAttachmentsAndAppendToModule,
  fetchDataAndUpdateStore,
  fetchDetailAndAppendToModule,
  fetchEmailAndAppendToModule,
  fetchNotesAndAppendToModule,
  fetchTagsAndAppendToModule,
} from '../../redux/actions';
import { getAccountById, getGridDataByModuleKey, getTagsByModule } from '../../redux/selector';
import { isLoadingByKey } from '../../shared/controls/methods/getLoadingState';
import apiServices from '../../services/service';
import { accountInputData } from '../../shared/constants';
import Loader from '../../shared/component/Loader';
import Notes from '../sharedChild/note/notes';
import Attachments from '../sharedChild/attachment/attachments';
import Email from '../sharedChild/email/emails';
import Opportunities from '../sharedChild/opportunity/opportunities';
import AccountContactsTab from './AccountContactsTab';
import AccountMemberAccountsTab from './AccountMemberAccountsTab';
import Timeline from '../sharedChild/timeline';
import Enrichment from '../leads/leadEnrichment';
import LeadCallModal from '../leads/LeadCallModal';
import { Ic } from "../../shared/component/GosIcon";

/* ---------- GrowthOS record icons (stroke, 24×24) ---------- */

const GOS_AVA_COLORS = ['#c96342', '#2e63a6', '#2c7a45', '#9a751c', '#6a4fae', '#b34480', '#54738f'];
const gosInitials = (name) =>
  String(name || '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0] || '')
    .join('')
    .toUpperCase() || '?';
const gosAvaColor = (name) =>
  GOS_AVA_COLORS[String(name || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0) % GOS_AVA_COLORS.length];

const val = (v) => (v === 0 || v ? v : null);

const InfoCard = ({ title, icon, rows }) => (
  <div className="gos-lr-card">
    <div className="gos-lr-card-head">
      <div className="gos-lr-card-title">
        <Ic name={icon} size={16} /> {title}
      </div>
    </div>
    <div className="gos-lr-card-rows">
      {rows.map(([label, value], i) => (
        <div className="gos-lr-drow" key={i}>
          <div className="gos-lr-dlabel">{label}</div>
          <div className={'gos-lr-dval' + (val(value) ? '' : ' muted')}>{val(value) ?? '—'}</div>
        </div>
      ))}
    </div>
  </div>
);
const DetailRow = ({ icon, label, children }) => (
  <div className="gos-lr-drow">
    <div className="gos-lr-dlabel">
      <Ic name={icon} size={15} /> {label}
    </div>
    <div className="gos-lr-dval gos-lr-dval-right">{children ?? '—'}</div>
  </div>
);
const Placeholder = ({ icon, title, text }) => (
  <div className="gos-lr-card gos-lr-placeholder">
    <div className="gos-lr-ph-ic">
      <Ic name={icon} size={22} />
    </div>
    <div className="gos-lr-ph-title">{title}</div>
    <div className="gos-lr-ph-text">{text}</div>
  </div>
);

/* ---------- Account tags editor (add / remove / create) ---------- */
const TAG_TONES = ['blue', 'green', 'amber', 'purple', 'rose', 'gray', 'red'];
const toneFor = (name) =>
  TAG_TONES[String(name || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0) % TAG_TONES.length];
const tagName = (t) => (Array.isArray(t?.name) ? t.name.join(', ') : t?.name || '');

const AccountTagEditor = ({ account }) => {
  const dispatch = useDispatch();
  const library = useSelector(getTagsByModule('accountList')) || [];
  const applied = account?.tags || [];
  const moduleId = account?.id;

  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    dispatch(fetchDataAndUpdateStore({ key: 'tagsByModule', inputData: 'Account', storeKey: 'accountList' }));
  }, [dispatch]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setQ('');
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        setQ('');
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    const t = setTimeout(() => inputRef.current?.focus(), 30);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      clearTimeout(t);
    };
  }, [open]);

  const refreshTags = () => {
    dispatch(fetchTagsAndAppendToModule({ moduleName: 'Account', id: moduleId }));
    dispatch(fetchDataAndUpdateStore({ key: 'tagsByModule', inputData: 'Account', storeKey: 'accountList' }));
  };

  const addTagByName = async (name) => {
    if (!moduleId || busy) return;
    setBusy(true);
    try {
      const res = await apiServices.createTags({ tagName: [name], module: 'Account', moduleId: [moduleId] });
      if (res.status === 200) {
        toast.success('Tag added successfully!', { position: 'top-right' });
        refreshTags();
      }
    } catch (err) {
      console.error('Error adding tag:', err);
      toast.error('Failed to add tag.', { position: 'top-right' });
    } finally {
      setBusy(false);
    }
  };

  const removeTag = async (tag) => {
    if (!moduleId || busy) return;
    setBusy(true);
    try {
      const res = await apiServices.deleteTagsByModuleId({ id: [tag.id], module: 'Account', moduleId: [moduleId] });
      if (res.status === 200) {
        toast.success('Tag removed successfully!', { position: 'top-right' });
        refreshTags();
      }
    } catch (err) {
      console.error('Error removing tag:', err);
      toast.error('Failed to remove tag.', { position: 'top-right' });
    } finally {
      setBusy(false);
    }
  };

  const toggleLibraryTag = (libTag) => {
    const name = tagName(libTag);
    const existing = applied.find((a) => tagName(a) === name);
    if (existing) removeTag(existing);
    else addTagByName(name);
  };

  const ql = q.trim();
  const filtered = ql ? library.filter((t) => tagName(t).toLowerCase().includes(ql.toLowerCase())) : library;
  const exact = library.some((t) => tagName(t).toLowerCase() === ql.toLowerCase());
  const nextTone = TAG_TONES[library.length % TAG_TONES.length];

  const create = () => {
    if (!ql || exact || busy) return;
    addTagByName(ql);
    setQ('');
    inputRef.current?.focus();
  };

  return (
    <div className="gos-lead-tags">
      <span className="gos-lead-tags-label">
        <Ic name="tag" size={13} /> Tags
      </span>
      {applied.map((t) => {
        const tone = toneFor(tagName(t));
        return (
          <span
            key={t.id}
            className="gos-lead-tag"
            style={{ background: `var(--gos-${tone}-bg)`, color: `var(--gos-${tone}-fg)` }}
          >
            <span className="gos-lt-dot" />
            {tagName(t)}
            <button type="button" className="gos-lt-x" title="Remove tag" onClick={() => removeTag(t)} disabled={busy}>
              <Ic name="x" size={13} />
            </button>
          </span>
        );
      })}
      <div className="gos-tag-add-wrap" ref={wrapRef}>
        <button type="button" className={'gos-tag-add' + (open ? ' on' : '')} onClick={() => setOpen((o) => !o)}>
          <Ic name="plus" size={14} /> Add tag
        </button>
        {open ? (
          <div className="gos-tag-pop">
            <div className="gos-tag-pop-search">
              <Ic name="search" size={14} />
              <input
                ref={inputRef}
                placeholder="Search or create…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && ql && !exact) create();
                }}
              />
            </div>
            <div className="gos-tag-pop-label">{ql ? 'Matching tags' : 'Apply tags'}</div>
            <div className="gos-tag-pop-list">
              {filtered.length === 0 && !ql ? <div className="gos-tag-pop-empty">No tags yet</div> : null}
              {filtered.map((t) => {
                const name = tagName(t);
                const on = applied.some((a) => tagName(a) === name);
                const tone = toneFor(name);
                return (
                  <button
                    type="button"
                    key={t.id}
                    className={'gos-tag-opt' + (on ? ' on' : '')}
                    onClick={() => toggleLibraryTag(t)}
                    disabled={busy}
                  >
                    <span className="gos-to-dot" style={{ background: `var(--gos-${tone}-fg)` }} />
                    {name}
                    <span className="gos-to-check">{on ? <Ic name="check" size={12} /> : null}</span>
                  </button>
                );
              })}
              {ql && filtered.length === 0 ? (
                <div className="gos-tag-pop-empty">No matches — create it below</div>
              ) : null}
            </div>
            {ql && !exact ? (
              <button type="button" className="gos-tag-create" onClick={create} disabled={busy}>
                <span
                  className="gos-tc-swatch"
                  style={{ background: `var(--gos-${nextTone}-bg)`, border: `1px solid var(--gos-${nextTone}-fg)` }}
                />
                Create <strong>{`“${ql}”`}</strong>
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
};

const ViewAccount = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const stateData = useSelector((state) => state);
  const storeLoading = isLoadingByKey(stateData, 'accountList');
  const [loading, setLoading] = useState(storeLoading);
  const [callOpen, setCallOpen] = useState(false);
  const [opportunityByModule, setOpportunityByModule] = useState({});
  const [accountContacts, setAccountContacts] = useState([]);
  const [accountContactsLoading, setAccountContactsLoading] = useState(false);
  const [enrichedDataLoading, setEnrichedDataLoading] = useState(false);
  const [enrichedGenerateReportLoading, setEnrichedGenerateReportLoading] = useState(false);
  const [showPopupAccount, setShowPopupAccount] = useState(false);
  const [accountEnrichedData, setAccountEnrichedData] = useState();
  const [refreshView, setRefreshView] = useState(false);
  const gridData = useSelector(getGridDataByModuleKey('accountList'));

  const searchParams = new URLSearchParams(location.search);
  const accountID = searchParams.get('id');

  let hasUpdateOpportunityPermission = true;
  let hasViewOpportunityPermission = true;
  let hasUpdateContactPermission = true;
  let hasViewContactPermission = true;
  const userUIControlPermissionOpportunity = JSON.parse(localStorage.getItem('userUIControlPermission'))?.opportunitymenu;
  const userUIControlPermissionContact = JSON.parse(localStorage.getItem('userUIControlPermission'))?.contactmenu;
  if (userUIControlPermissionOpportunity) {
    hasViewOpportunityPermission = userUIControlPermissionOpportunity?.opportunityviewscreen ? userUIControlPermissionOpportunity.opportunityviewscreen.canView : true;
    hasUpdateOpportunityPermission = userUIControlPermissionOpportunity?.opportunityupdatescreen ? userUIControlPermissionOpportunity.opportunityupdatescreen.canView : true;
  }
  if (userUIControlPermissionContact) {
    hasViewContactPermission = userUIControlPermissionContact?.contactviewscreen ? userUIControlPermissionContact.contactviewscreen.canView : true;
    hasUpdateContactPermission = userUIControlPermissionContact?.contactupdatescreen ? userUIControlPermissionContact.contactupdatescreen.canView : true;
  }

  const [accountLocalData, setAccountLocalData] = useState(location.state || {});
  const accountStoreData = useSelector((state) => getAccountById(state, location.state?.id || accountID));

  useEffect(() => {
    setAccountLocalData((prev) => ({ ...prev, ...accountStoreData }));
  }, [accountStoreData]);

  const handleOpenEditForm = () => {
    navigate('/accounts/editAccount', { state: { ...accountLocalData, type: 'view', from: 'view' } });
  };

  const openPopupAccount = () => setShowPopupAccount(true);
  const closePopupAccount = () => setShowPopupAccount(false);

  const handleClickDataEnrichment = () => {
    if (!accountLocalData.website) {
      toast.error('Please fill in the Website field.', { position: 'top-right' });
      return;
    }
    setEnrichedDataLoading(true);
    const data = {
      CompanyName: `${accountLocalData.name}`,
      CompanyDomain: `${accountLocalData.website}`,
      EnrichmentType: 'accounts',
    };
    const fetchEnrichedData = () => {
      apiServices
        .getEnrichedData(data)
        .then((response) => {
          if (response.status === 200) {
            setAccountEnrichedData(response.data?.enriched_data);
            setEnrichedDataLoading(false);
            apiServices
              .updateEnrichedFlag({ id: accountLocalData.id }, 'Account')
              .then((res) => {
                if (res.status === 200) {
                  openPopupAccount();
                  dispatch(
                    fetchDataAndUpdateStore({
                      key: 'accountList',
                      inputData: gridData?.inputPayload ? gridData.inputPayload : accountInputData,
                    })
                  );
                }
              })
              .catch((error) => {
                console.error('Error updating enriched flag:', error);
                toast.error('Failed to update enriched flag.', { position: 'top-right' });
              });
          } else if (response.status === 202) {
            toast.info('Enrichment Initiated! Retrying in 2 minutes...', { position: 'top-right' });
            setTimeout(fetchEnrichedData, 120000);
          }
        })
        .catch((error) => {
          console.error('Error while fetching enriched data:', error);
          const errorMsg = error?.response?.data?.error || 'An unexpected error occurred.';
          toast.error(errorMsg, { position: 'top-right' });
          setEnrichedDataLoading(false);
        });
    };
    fetchEnrichedData();
  };

  const handleClickReportGenerated = (flag = false) => {
    if (!accountLocalData.website) {
      toast.error('Please fill in the Website field.', { position: 'top-right' });
      return;
    }
    setEnrichedGenerateReportLoading(true);
    const data = {
      CompanyName: `${accountLocalData.name}`,
      CompanyDomain: `${accountLocalData.website}`,
      EnrichmentType: 'accounts',
      Regenerate: flag,
    };
    const fetchGenerateReport = () => {
      apiServices
        .generateReport(data)
        .then((response) => {
          if (response.status === 200) {
            const htmlContent = response.data?.Report;
            const blob = new Blob([htmlContent], { type: 'text/html' });
            const url = URL.createObjectURL(blob);
            window.open(url, '_blank');
            setEnrichedGenerateReportLoading(false);
            apiServices
              .updateGenerateReportFlag({ id: accountLocalData.id }, 'Account')
              .then((res) => {
                if (res.status === 200) {
                  dispatch(
                    fetchDataAndUpdateStore({
                      key: 'accountList',
                      inputData: gridData?.inputPayload ? gridData.inputPayload : accountInputData,
                    })
                  );
                }
              })
              .catch((error) => {
                console.error('Error updating generate report flag:', error);
                toast.error('Failed to update generate report flag.', { position: 'top-right' });
              });
          } else if (response.status === 202) {
            toast.info('Generate Report Initiated! Retrying in 2 minutes...', { position: 'top-right' });
            setTimeout(fetchGenerateReport, 120000);
          }
        })
        .catch((error) => {
          console.error('Error while fetching generate report data:', error);
          toast.error('An unexpected error occurred.', { position: 'top-right' });
          setEnrichedGenerateReportLoading(false);
        });
    };
    fetchGenerateReport();
  };

  const handleUpdateForm = async (formData) => {
    setLoading(true);
    try {
      const response = await apiServices.updateAccountList(formData);
      if (response.status === 200) {
        await dispatch(
          fetchDataAndUpdateStore({
            key: 'accountList',
            inputData: gridData?.inputPayload ? gridData?.inputPayload : accountInputData,
          })
        );
        closePopupAccount();
        setRefreshView((prev) => !prev);
      } else {
        setLoading(false);
        const errorMessages = [];
        for (const key in response.errors) {
          if (response.errors.hasOwnProperty(key)) errorMessages.push(...response.errors[key]);
        }
        errorMessages.forEach((m) => toast.error(m, { position: 'top-right' }));
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('An unexpected error occurred.', { position: 'top-right' });
      setLoading(false);
    }
  };

  // opportunities (themed Opportunities tab) for this account
  useEffect(() => {
    if (!accountLocalData?.id) return;
    setLoading(true);
    apiServices
      .getOpportunityByModule({ moduleName: 'Account', id: accountLocalData?.id })
      .then((response) => {
        if (response.status === 200) setOpportunityByModule(response.data);
        setLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching opportunities:', error);
        setLoading(false);
      });
  }, [accountLocalData?.id]);

  // contacts (themed Contacts tab) for this account — fetched once here so the
  // tab-bar count badge and the AccountContactsTab card share a single API call
  useEffect(() => {
    if (!accountLocalData?.id) return;
    setAccountContactsLoading(true);
    apiServices
      .getContactByModule({ moduleName: 'Account', id: accountLocalData?.id })
      .then((response) => {
        if (response.status === 200) setAccountContacts(response.data?.contacts || []);
        setAccountContactsLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching account contacts:', error);
        setAccountContactsLoading(false);
      });
  }, [accountLocalData?.id]);

  useEffect(() => {
    setLoading(storeLoading);
  }, [storeLoading]);

  useEffect(() => {
    const accountId = location.state?.id || accountID;
    if (accountId) {
      dispatch(fetchDetailAndAppendToModule({ moduleName: 'Account', id: accountId, localDetail: location.state || null }));
      dispatch(fetchNotesAndAppendToModule({ moduleName: 'Account', id: accountId }));
      dispatch(fetchAttachmentsAndAppendToModule({ moduleName: 'Account', id: accountId }));
      dispatch(fetchEmailAndAppendToModule({ moduleName: 'Account', id: accountId }));
      dispatch(fetchTagsAndAppendToModule({ moduleName: 'Account', id: accountId }));
    }
  }, [location, accountLocalData?.id, refreshView]);

  const ad = accountLocalData || {};
  const acctName = ad.name || 'Account';
  const cityState = [ad.city, ad.state].filter(Boolean).join(', ');
  const chips = [
    ad.accountType && { icon: 'briefcase', label: ad.accountType },
    ad.industry && { icon: 'building', label: ad.industry },
    cityState && { icon: 'location', label: cityState },
    ad.owner && { icon: 'user', label: ad.owner },
  ].filter(Boolean);

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'grid' },
    { id: 'activities', label: 'Activities', icon: 'clock' },
    { id: 'notes', label: 'Notes', icon: 'note', count: ad.notesList?.length },
    { id: 'attachments', label: 'Attachments', icon: 'paperclip', count: ad.attachments?.length },
    hasViewOpportunityPermission && { id: 'opportunities', label: 'Opportunities', icon: 'opportunity' },
    hasViewContactPermission && { id: 'contacts', label: 'Contacts', icon: 'users', count: accountContacts.length },
    { id: 'childAccounts', label: 'Member Account', icon: 'building' },
    { id: 'emails', label: 'Emails', icon: 'mail', count: ad.emails?.length },
  ].filter(Boolean);

  const overview = (
    <div className="gos-lr-grid2">
      <InfoCard
        title="Company Information"
        icon="building"
        rows={[
          ['Account Name', val(acctName)],
          ['Account Type', val(ad.accountType)],
          ['Industry', val(ad.industry)],
          ['Parent Account', val(ad.parentAccount)],
        ]}
      />
      <InfoCard
        title="Contact & Ownership"
        icon="phone"
        rows={[
          ['Phone', val(ad.phone)],
          ['Alternate Phone', val(ad.alternatePhone)],
          ['Website', val(ad.website)],
          ['Company LinkedIn', val(ad.companyLinkedIn)],
          ['Owner', val(ad.owner)],
        ]}
      />
      <InfoCard
        title="Business & Technology"
        icon="briefcase"
        rows={[
          ['Employees', val(ad.employees)],
          ['Annual Revenue', val(ad.annualRevenue)],
          ['No. of Beds', val(ad.noOfBeds)],
          ['Technology Details', val(ad.technologyDetails)],
          ['Description', val(ad.description)],
        ]}
      />
      <InfoCard
        title="Address Information"
        icon="location"
        rows={[
          ['Street', val(ad.street)],
          ['City', val(ad.city)],
          ['State', val(ad.state)],
          ['Zip Code', val(ad.zipCode)],
          ['Country', val(ad.country)],
        ]}
      />
    </div>
  );

  const detailsPanel = (
    <div className="gos-lr-card gos-lr-sticky">
      <div className="gos-lr-card-head">
        <div className="gos-lr-card-title">
          <Ic name="info" size={16} /> Details
        </div>
        <button type="button" className="gos-lr-iconbtn" onClick={handleOpenEditForm} title="Edit account">
          <Ic name="edit" size={14} />
        </button>
      </div>
      <div className="gos-lr-sec">
        <div className="gos-lr-sec-title">Company</div>
        <div className="gos-lr-card-rows">
          <DetailRow icon="building" label="Name">{val(acctName)}</DetailRow>
          <DetailRow icon="briefcase" label="Type">{val(ad.accountType)}</DetailRow>
          <DetailRow icon="info" label="Industry">{val(ad.industry)}</DetailRow>
          <DetailRow icon="user" label="Owner">{val(ad.owner)}</DetailRow>
          <DetailRow icon="phone" label="Phone">{val(ad.phone || ad.alternatePhone)}</DetailRow>
          <DetailRow icon="link" label="Website">
            {ad.website ? <span className="gos-lr-link">{ad.website}</span> : '—'}
          </DetailRow>
        </div>
      </div>
      <div className="gos-lr-divider" />
      <div className="gos-lr-sec">
        <div className="gos-lr-sec-title">Location</div>
        <div className="gos-lr-card-rows">
          <DetailRow icon="location" label="City">{val(ad.city)}</DetailRow>
          <DetailRow icon="globe" label="State">{val(ad.state)}</DetailRow>
          <DetailRow icon="globe" label="Country">{val(ad.country)}</DetailRow>
        </div>
      </div>
    </div>
  );

  let tabBody;
  if (activeTab === 'overview') tabBody = overview;
  else if (activeTab === 'activities') tabBody = <Timeline localData={accountLocalData} module={'Account'} />;
  else if (activeTab === 'notes') tabBody = <Notes leadLocalData={accountLocalData} module={'Account'} />;
  else if (activeTab === 'attachments') tabBody = <Attachments localData={accountLocalData} module={'Account'} />;
  else if (activeTab === 'opportunities')
    tabBody = hasViewOpportunityPermission ? (
      <Opportunities localData={opportunityByModule} module={'Account'} hasUpdateOpportunityPermission={hasUpdateOpportunityPermission} />
    ) : (
      <Placeholder icon="opportunity" title="Opportunities" text="You don't have permission to view opportunities for this account." />
    );
  else if (activeTab === 'contacts')
    tabBody = hasViewContactPermission ? (
      <AccountContactsTab contacts={accountContacts} loading={accountContactsLoading} canAdd={hasUpdateContactPermission} />
    ) : (
      <Placeholder icon="users" title="Contacts" text="You don't have permission to view contacts for this account." />
    );
  else if (activeTab === 'childAccounts') tabBody = <AccountMemberAccountsTab account={accountLocalData} />;
  else if (activeTab === 'emails') tabBody = <Email localData={accountLocalData} module={'Account'} />;

  return (
    <>
      {loading && <Loader />}

      <div className="ViewLeadPageOuter gos-leadrec">
        {/* back row */}
        <div className="gos-lr-backrow">
          <button type="button" className="gos-lr-ghost-btn" onClick={() => navigate('/accounts')}>
            <Ic name="chevL" size={15} /> Accounts list
          </button>
          {ad.id ? (
            <div className="gos-lr-leadid">
              Account <span>{ad.id}</span>
            </div>
          ) : null}
        </div>

        {/* header card */}
        <div className="gos-lr-card gos-lr-header">
          <div className="gos-lr-header-top">
            <div className="gos-lr-header-l">
              <span className="gos-lr-ava" style={{ background: gosAvaColor(acctName) }}>
                {gosInitials(acctName)}
              </span>
              <div className="gos-lr-header-meta">
                <div className="gos-lr-name">{acctName}</div>
                <div className="gos-lr-subline">
                  <Ic name="briefcase" size={14} />
                  {[ad.accountType, ad.industry].filter(Boolean).join(' · ') || '—'}
                  {ad.phone ? (
                    <>
                      <span className="gos-lr-dot">·</span>
                      <Ic name="phone" size={14} />
                      {ad.phone}
                    </>
                  ) : null}
                </div>
              </div>
            </div>
            <div className="gos-lr-actions">
              {ad.phone ? (
                <button type="button" className="gos-lr-contact-btn" onClick={() => setCallOpen(true)}>
                  <Ic name="phone" size={16} /> Call
                </button>
              ) : null}
              <button
                type="button"
                className="gos-lr-iconbtn gos-lr-iconbtn-bordered"
                onClick={() => setMenuOpen((o) => !o)}
                aria-label="More actions"
              >
                <Ic name="dots" size={18} />
              </button>
              {menuOpen ? (
                <>
                  <div className="gos-lr-menu-backdrop" onClick={() => setMenuOpen(false)} />
                  <div className="gos-lr-menu">
                    <button type="button" className="gos-lr-menu-item" onClick={() => { setMenuOpen(false); handleOpenEditForm(); }}>
                      <Ic name="edit" size={15} /> Edit Account
                    </button>
                    <button
                      type="button"
                      className="gos-lr-menu-item"
                      onClick={() => { setMenuOpen(false); handleClickDataEnrichment(); }}
                      disabled={enrichedDataLoading}
                    >
                      <Ic name="zap" size={15} />
                      {ad.isEnriched ? 'Refresh Enriched Data' : 'Get Enriched Data'}
                    </button>
                    <button
                      type="button"
                      className="gos-lr-menu-item"
                      onClick={() => { setMenuOpen(false); handleClickReportGenerated(); }}
                      disabled={enrichedGenerateReportLoading}
                    >
                      <Ic name="note" size={15} />
                      {ad.isAccountReportGenerated ? 'View Report' : 'Generate Report'}
                    </button>
                    {ad.isAccountReportGenerated ? (
                      <button
                        type="button"
                        className="gos-lr-menu-item"
                        onClick={() => { setMenuOpen(false); handleClickReportGenerated(true); }}
                        disabled={enrichedGenerateReportLoading}
                      >
                        <Ic name="refresh" size={15} /> Regenerate Report
                      </button>
                    ) : null}
                  </div>
                </>
              ) : null}
            </div>
          </div>
          {chips.length ? (
            <div className="gos-lr-chips">
              {chips.map((c, i) => (
                <span className="gos-lr-chip" key={i}>
                  <Ic name={c.icon} size={14} /> {c.label}
                </span>
              ))}
            </div>
          ) : null}
          <AccountTagEditor account={ad} />
        </div>

        {/* tabs */}
        <div className="gos-lr-tabs">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              className={'gos-lr-tab' + (activeTab === t.id ? ' active' : '')}
              onClick={() => setActiveTab(t.id)}
            >
              <Ic name={t.icon} size={15} /> {t.label}
              {t.count != null ? <span className="gos-lr-count">{t.count}</span> : null}
            </button>
          ))}
        </div>

        {/* body */}
        {activeTab === 'overview' ? (
          overview
        ) : (
          <div className="gos-lr-2col">
            <div className="gos-lr-main">{tabBody}</div>
            {detailsPanel}
          </div>
        )}
      </div>

      {callOpen && (
        <LeadCallModal
          lead={{ fullName: acctName, phone: ad.phone, state: ad.state }}
          onClose={() => setCallOpen(false)}
        />
      )}
      {showPopupAccount && (
        <Enrichment
          handleClose={closePopupAccount}
          localData={accountLocalData}
          enrichedData={accountEnrichedData}
          handleSave={handleUpdateForm}
          loading={loading}
        />
      )}
    </>
  );
};

export default ViewAccount;
